/**
 * The app's half of running a browser tool from the admin portal's Tools page
 * (spec 2026-10-18 AI companion growth architecture, 11.2). A browser tool acts
 * on a live workspace, so it runs here, in the app, not in the portal.
 *
 * The portal opens the app in a new tab. Once the app shell is up, this posts
 * `{type: 'edge-admin-bridge-ready'}` to the window that opened it, naming the
 * portal's origin as the only one that may receive it. The portal answers with
 * `{type: 'edge-admin-run', tool, args, nonce, ticket}`, and the app:
 *
 * 1. takes the message only from the configured portal origin, exactly, only
 *    from the window that opened this tab (`event.source === window.opener`,
 *    so another tab or frame on the portal origin cannot ask), and only in
 *    that shape; it ignores everything else;
 * 2. refuses a nonce it has already received. A refusal is dropped, not
 *    answered: an answer would race the real result at the portal;
 * 3. asks companion-api whether the ticket holds for this tool and these
 *    arguments (signed for an admin, a minute old at most, single use; see
 *    features/tools-bridge.ts there), and takes only its `{ok: true}` as a yes.
 *    The check runs on the app's own sign-in, which need not be an admin's:
 *    the ticket carries the admin;
 * 4. runs the call the way the AI's calls run: a `tool-request` on the
 *    companion's event bus, which use-companion-actions.ts answers with the
 *    confirm card, the executor and Undo. The request carries access 'change',
 *    as the trader's "Do it" does: an operator's run is an explicit request, so
 *    a change tool runs even when the AI's last turn could not change anything.
 *    A change that asks first always shows its card, "Apply changes without
 *    asking" notwithstanding. What lets it run is the gate above: the exact
 *    portal origin and window, and a ticket an admin minted for this tool and
 *    these arguments, once, within a minute; nothing reaches the event bus
 *    without all of them;
 * 5. posts `{type: 'edge-admin-run-result', nonce, ok, refused, result, error, ms}`
 *    back to the window that asked, again naming the portal's origin, then
 *    lets go of it (`window.opener = null`): each run opens its own tab, so
 *    this one takes no further runs.
 *    `refused` says the run stopped at the ticket, before the tool, so the
 *    portal does not keep it as a run.
 *
 * Nothing goes in the URL, and a ticket is never logged. The bridge is on only
 * where `ADMIN_PORTAL_ORIGIN` is set (admin-tool-bridge-host.tsx).
 */
import { z } from 'zod'
import type { CompanionEvent, ToolCall, ToolResult } from '@edge/edge-ai'
import { companionClient } from '@/services/api/api-client'
import { ApiError } from '@/services/api/errors'

export const BRIDGE_READY = 'edge-admin-bridge-ready'
export const BRIDGE_RUN = 'edge-admin-run'
export const BRIDGE_RESULT = 'edge-admin-run-result'

const RunMessageSchema = z.object({
  type: z.literal(BRIDGE_RUN),
  tool: z
    .string()
    .max(64)
    .regex(/^[a-z][a-z0-9_]*$/),
  args: z.record(z.string(), z.unknown()),
  nonce: z.string().min(16).max(128),
  ticket: z.string().min(1).max(4096),
})

type RunMessage = z.infer<typeof RunMessageSchema>

/** What a run came to, as the portal reads it. */
interface RunOutcome {
  ok: boolean
  refused: boolean
  result: unknown
  error: string | null
  ms: number
}

/** The parts of the app's window the bridge uses. */
export interface BridgeWindow {
  addEventListener(type: 'message', listener: (event: MessageEvent) => void): void
  removeEventListener(type: 'message', listener: (event: MessageEvent) => void): void
  /** The portal's window; set to null once a run is answered. */
  opener: { postMessage(message: unknown, targetOrigin: string): void } | null
}

export interface AdminToolBridgeDeps {
  /** The admin portal's origin: scheme, host and port. */
  portalOrigin: string
  /** The app's window: where the portal's messages arrive, and whose opener the portal is. */
  win: BridgeWindow
  /** companion-api's ticket check; throws when the ticket does not hold. */
  verify: (body: { ticket: string; tool: string; args: Record<string, unknown> }) => Promise<void>
  /** Runs one call the way the AI's calls run, and resolves with its result. */
  run: (call: ToolCall) => Promise<ToolResult>
  /** Milliseconds, for the run's time; injectable for tests. */
  now?: () => number
}

/** The origin a configured value names, or null when it names none. */
function originOf(value: string): string | null {
  try {
    const origin = new URL(value).origin
    return origin === 'null' ? null : origin
  } catch {
    return null
  }
}

/** A result's content as the value it encodes, or its text when it is not JSON. */
function contentValue(content: string): unknown {
  try {
    return JSON.parse(content)
  } catch {
    return content
  }
}

/** Why the ticket check failed, in words the portal can show the operator. */
function refusalOf(error: unknown): string {
  if (error instanceof ApiError) {
    const detail = (error.body as { detail?: unknown } | undefined)?.detail
    return typeof detail === 'string'
      ? `The app refused the run: ${detail}`
      : `The app could not check the ticket (HTTP ${error.status})`
  }
  return `The app could not check the ticket: ${error instanceof Error ? error.message : String(error)}`
}

/**
 * Listens for the portal's run messages and tells the portal the app is ready.
 * Returns the uninstall. Does nothing for a portal origin that does not parse.
 */
export function installAdminToolBridge(deps: AdminToolBridgeDeps): () => void {
  const portal = originOf(deps.portalOrigin)
  if (!portal) return () => undefined
  const now = deps.now ?? (() => performance.now())
  const received = new Set<string>()

  async function handle(message: RunMessage, reply: (outcome: RunOutcome) => void) {
    try {
      await deps.verify({ ticket: message.ticket, tool: message.tool, args: message.args })
    } catch (error) {
      reply({ ok: false, refused: true, result: null, error: refusalOf(error), ms: 0 })
      return
    }
    const started = now()
    let result: ToolResult
    try {
      result = await deps.run({
        tool_use_id: `admin_run_${message.nonce}`,
        name: message.tool,
        input: message.args,
      })
    } catch (error) {
      reply({
        ok: false,
        refused: false,
        result: null,
        error: error instanceof Error ? error.message : String(error),
        ms: Math.round(now() - started),
      })
      return
    }
    const ms = Math.round(now() - started)
    reply(
      result.success
        ? { ok: true, refused: false, result: contentValue(result.content), error: null, ms }
        : { ok: false, refused: false, result: null, error: result.content, ms },
    )
  }

  const onMessage = (event: MessageEvent) => {
    if (event.origin !== portal) return
    // Only the window that opened this tab: another tab or frame on the portal
    // origin is not the run's window. Null once a run has been answered.
    const opener = deps.win.opener
    if (!opener || event.source !== opener) return
    const parsed = RunMessageSchema.safeParse(event.data)
    if (!parsed.success) return
    const message = parsed.data
    if (received.has(message.nonce)) {
      console.warn('[admin-tool-bridge] refused a run message it had already received')
      return
    }
    received.add(message.nonce)
    void handle(message, (outcome) => {
      opener.postMessage({ type: BRIDGE_RESULT, nonce: message.nonce, ...outcome }, portal)
      // Each run opens its own tab: this one takes no further runs, and keeps
      // no handle on the portal's window.
      deps.win.opener = null
    })
  }

  deps.win.addEventListener('message', onMessage)
  deps.win.opener?.postMessage({ type: BRIDGE_READY }, portal)
  return () => deps.win.removeEventListener('message', onMessage)
}

/**
 * companion-api's ticket check, on the app's own sign-in. Throws `ApiError`
 * when it does not hold, and throws as well unless the answer is the route's
 * own `{ok: true}`: a 2xx alone says nothing about the ticket.
 */
export async function verifyBridgeTicket(body: {
  ticket: string
  tool: string
  args: Record<string, unknown>
}): Promise<void> {
  const answer = await companionClient.post<unknown>('/v1/tools/bridge-tickets/verify', body)
  if ((answer as { ok?: unknown } | null | undefined)?.ok !== true) {
    throw new Error('companion-api did not say the ticket holds')
  }
}

/**
 * Runs one call the way the AI's calls run: a `tool-request` on the companion's
 * event bus. use-companion-actions.ts, mounted once in the app shell, answers it
 * with the confirm card, the executor and Undo.
 *
 * The run is its own turn (its own id and signal), so the action hook opens a
 * fresh automation turn for it: an earlier AI turn in the tab that was stopped
 * cannot fail it with "Task stopped". The signal is aborted once the result is
 * in, which closes that turn, since no assistant message will. It runs with
 * access 'change', whatever the AI's last turn was; only the bridge's checked
 * runs come through here.
 */
export function runThroughCompanion(
  events: { emit(event: CompanionEvent): void },
  call: ToolCall,
): Promise<ToolResult> {
  const controller = new AbortController()
  return new Promise<ToolResult>((resolve) => {
    events.emit({
      type: 'tool-request',
      toolCalls: [call],
      resolve: (results) =>
        resolve(
          results.find((result) => result.tool_use_id === call.tool_use_id) ?? {
            tool_use_id: call.tool_use_id,
            success: false,
            is_error: true,
            content: 'No result for this call.',
          },
        ),
      turnId: `admin-tool-run:${call.tool_use_id}`,
      signal: controller.signal,
      access: 'change',
    })
  }).finally(() => controller.abort())
}
